import authApi from "../api/auth.api"
import { AuthUserInterface, TokensInterface } from "../model/auth.types"
import { useAuth } from "./useAuth"
import { MANUAL_LOGOUT_KEY } from "../model/constants"
import { isSalesSystemAccount } from './salesAccess'

const useAuthActions = () => {
  const { setLoadingState, setErrorState, setIsAuth, setIsHead, setUser, setPinUnlocked, user } = useAuth()

  const handleAuthSuccess = (res: {user: AuthUserInterface; tokens: TokensInterface;}) => {
    setIsAuth(true)
    setIsHead(isSalesSystemAccount(res.user) || res.user.bx.sub_users.length > 0)
    setUser(res.user)
    setPinUnlocked(false)
    localStorage.setItem('token', res.tokens.accessToken.token)
  }

  const login = async (email: string, password: string): Promise<{user: AuthUserInterface; tokens: TokensInterface;} | null> => {
    setLoadingState("login", true)
    setErrorState("login", null)
    try {
      const res = await authApi.login(email, password)
      localStorage.removeItem(MANUAL_LOGOUT_KEY)
      handleAuthSuccess(res.data)
      return res.data
    } catch (error) {
      setErrorState("login", error as string)
      return null
    } finally {
      setLoadingState("login", false)
    }
  }

  const refresh = async (): Promise<{user: AuthUserInterface; tokens: TokensInterface;} | null> => {
    setLoadingState("refresh", true)
    setErrorState("refresh", null)
    try {
      const res = await authApi.refresh()
      handleAuthSuccess(res.data)
      return res.data
    } catch (error) {
      setErrorState("refresh", error as string)
      return null
    } finally {
      setLoadingState("refresh", false)
    }
  }

  const logout = async () => {
    setLoadingState("logout", true)
    localStorage.setItem(MANUAL_LOGOUT_KEY, '1')
    try {
      await authApi.logout()
    } catch {
      // Игнорируем ошибку — выходим локально в любом случае
    } finally {
      localStorage.removeItem("token")
      setIsHead(false)
      setIsAuth(false)
      setUser(null)
      setPinUnlocked(false)
      setLoadingState("logout", false)
      window.location.href = "/"
    }
  }

  const setupPin = async (pin: string) => {
    const response = await authApi.setupPin(pin)
    localStorage.setItem('token', response.data.accessToken.token)
    if (user) setUser({ ...user, pin_configured: true })
    setPinUnlocked(true)
  }

  const verifyPin = async (pin: string) => {
    const response = await authApi.verifyPin(pin)
    localStorage.setItem('token', response.data.accessToken.token)
    setPinUnlocked(true)
  }

  const resetPin = async (password: string, pin: string) => {
    const response = await authApi.resetPin(password, pin)
    localStorage.setItem('token', response.data.accessToken.token)
    setPinUnlocked(true)
  }

  return { login, refresh, logout, setupPin, verifyPin, resetPin }
}

export { useAuthActions }
